import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ScrollToTop from "@/components/ScrollToTop";
import FeedRecommendationWidget from "@/components/FeedRecommendationWidget";
import VisitorTracker from "@/components/VisitorTracker";
import CookieConsent from "@/components/CookieConsent";
import CopyProtection from "@/components/CopyProtection";
import EmailLinkHandler from "@/components/EmailLinkHandler";
import Index from "./pages/Index";
import PageLoader from "@/components/PageLoader";

const lazyPages = {
  "/products": () => import("./pages/Products"),
  "/product/:id": () => import("./pages/ProductDetails"),
  "/about": () => import("./pages/About"),
  "/privacy-policy": () => import("./pages/PrivacyPolicy"),
  "/terms-of-service": () => import("./pages/TermsOfService"),
  "/faq": () => import("./pages/FAQ"),
  "/gallery": () => import("./pages/Gallery"),
  "/blog": () => import("./pages/Blog"),
  "/blog/:id": () => import("./pages/BlogPost"),
  "/downloads": () => import("./pages/Downloads"),
  "/careers": () => import("./pages/Careers"),
  "/resumes": () => import("./pages/Resumes"),
};

// Prefetch helper — call on link hover to start loading the chunk early
export const prefetchPage = (path: string) => {
  const loader = (lazyPages as Record<string, (() => Promise<unknown>) | undefined>)[path];
  loader?.();
};

const Products = lazy(lazyPages["/products"]);
const ProductDetails = lazy(lazyPages["/product/:id"]);
const About = lazy(lazyPages["/about"]);
const PrivacyPolicy = lazy(lazyPages["/privacy-policy"]);
const TermsOfService = lazy(lazyPages["/terms-of-service"]);
const FAQ = lazy(lazyPages["/faq"]);
const Gallery = lazy(lazyPages["/gallery"]);
const Blog = lazy(lazyPages["/blog"]);
const BlogPost = lazy(lazyPages["/blog/:id"]);
const Downloads = lazy(lazyPages["/downloads"]);
const Careers = lazy(lazyPages["/careers"]);
const Resumes = lazy(lazyPages["/resumes"]);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <CopyProtection />
        <EmailLinkHandler />
        <FeedRecommendationWidget />
        <VisitorTracker />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/products" element={<Products />} />
            <Route path="/about" element={<About />} />
            
            <Route path="/product/:id" element={<ProductDetails />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:id" element={<BlogPost />} />
            <Route path="/downloads" element={<Downloads />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/resumes" element={<Resumes />} />
          </Routes>
        </Suspense>
        <CookieConsent />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
